import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return 'unknown time';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

// Union of keys across every row, so a forensic table shows whatever the
// backend put on each document instead of a hand-picked subset that could
// silently hide a field a writer started stamping later -- same convention
// as MatchLifecycleProbePanel's MatchHistoryTable.
function rowColumns(rows) {
  const cols = new Set();
  rows.forEach((row) => Object.keys(row).forEach((k) => cols.add(k)));
  return Array.from(cols).sort((a, b) => (a === 'id' ? -1 : b === 'id' ? 1 : a.localeCompare(b)));
}

function fmtCell(col, value) {
  if (value == null) return '-';
  if (col.endsWith('_at') || col === 'first_seen' || col === 'last_seen') return fmtTimestamp(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function RawSection({ title, testId, section }) {
  const rows = section?.rows || [];
  const columns = rowColumns(rows);
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={testId}>
      <div className="text-sm font-medium">{title} ({section?.count ?? 0})</div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
          {section?.empty_message || 'No records found.'}
        </div>
      ) : (
        <div className="border rounded overflow-x-auto max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                {columns.map((col) => (
                  <th key={col} className="text-left px-2 py-1 font-medium whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id || i} className="border-b last:border-0 hover:bg-muted/50">
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1 whitespace-nowrap">{fmtCell(col, row[col])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function WriteProvenancePanel() {
  const [transactionId, setTransactionId] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const id = transactionId.trim();
    if (!id) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await matchingAPI.writeProvenance(id, windowStart.trim() || undefined, windowEnd.trim() || undefined);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const timeline = result?.timeline || [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Write Provenance Probe</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Enter a transaction id to see every timestamped trace the app's own data
          holds for who wrote to it and when: confirm/revert events, reconciliation memory,
          the pointer-heal and ownership-conflict audit logs, job runs (sync_logs + the
          auto-match chain), and statement-payout / recurring-cost matches, in a time window --
          default 19 Jul 12:00 to 20 Jul 04:30 UTC, overridable below.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            data-testid="write-provenance-tx-input"
            placeholder="Transaction id"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            className="max-w-xs"
          />
          <Input
            data-testid="write-provenance-window-start-input"
            placeholder="Window start (ISO, optional)"
            value={windowStart}
            onChange={(e) => setWindowStart(e.target.value)}
            className="max-w-[220px]"
          />
          <Input
            data-testid="write-provenance-window-end-input"
            placeholder="Window end (ISO, optional)"
            value={windowEnd}
            onChange={(e) => setWindowEnd(e.target.value)}
            className="max-w-[220px]"
          />
          <Button data-testid="write-provenance-run" onClick={run} disabled={loading || !transactionId.trim()} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Probe
          </Button>
        </div>

        {result && (
          <div className="space-y-3" data-testid="write-provenance-result">
            {!result.transaction_found && (
              <div className="border border-amber-300 rounded-lg p-2 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/10 text-xs" data-testid="write-provenance-tx-missing">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                No transaction document exists for id "{result.transaction_id}" -- any rows below
                came from audit collections that can outlive the document itself.
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Window: {result.window_start} to {result.window_end}
            </div>

            <div className="border rounded-lg p-3 space-y-2" data-testid="write-provenance-timeline">
              <div className="text-sm font-medium">Timeline ({timeline.length})</div>
              {timeline.length === 0 ? (
                <div className="text-xs text-muted-foreground" data-testid="write-provenance-timeline-empty">
                  No timestamped events, memory rows, heal/ownership-conflict logs, job runs, or
                  audit fields were found for this transaction in the given window.
                </div>
              ) : (
                <ol className="space-y-1 text-xs">
                  {timeline.map((entry, i) => (
                    <li key={i} className="border-b last:border-0 pb-1 flex gap-2">
                      <span className="text-muted-foreground whitespace-nowrap">{fmtTimestamp(entry.timestamp)}</span>
                      <span className="font-mono text-muted-foreground/70 whitespace-nowrap">[{entry.source}]</span>
                      <span>{entry.description}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <RawSection title="match_events" testId="write-provenance-match-events" section={result.match_events} />
              <RawSection title="reconciliation_memory" testId="write-provenance-memory" section={result.reconciliation_memory} />
              <RawSection title="reconciliation_heal_events" testId="write-provenance-heal-events" section={result.reconciliation_heal_events} />
              <RawSection title="reconciliation_ownership_conflicts" testId="write-provenance-ownership-conflicts" section={result.reconciliation_ownership_conflicts} />
              <RawSection title="sync_logs (in window)" testId="write-provenance-sync-logs" section={result.sync_logs} />
              <RawSection title="chain_runs (in window)" testId="write-provenance-chain-runs" section={result.chain_runs} />
              <RawSection title="statement_payout_matches" testId="write-provenance-statement-payout-matches" section={result.statement_payout_matches} />
              <RawSection title="recurring_cost_matches" testId="write-provenance-recurring-cost-matches" section={result.recurring_cost_matches} />
            </div>

            <div className="border rounded-lg p-3 space-y-1 text-xs" data-testid="write-provenance-tx-audit-fields">
              <div className="font-medium">Transaction document audit fields</div>
              {Object.keys(result.transaction_audit_fields?.fields || {}).length === 0 ? (
                <div className="text-muted-foreground" data-testid="write-provenance-tx-audit-fields-empty">
                  {result.transaction_audit_fields?.empty_message || 'No audit-shaped timestamp fields present on this transaction document.'}
                </div>
              ) : (
                Object.entries(result.transaction_audit_fields.fields).map(([field, value]) => (
                  <div key={field}><span className="text-muted-foreground">{field}:</span> {fmtTimestamp(value)}</div>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
